<template>
  <div class="tw-flex tw-gap-2">
    <TooltipProvider>
      <Tooltip v-for="action in actions.filter(action => action.show())" :key="action.label">
        <TooltipTrigger as-child>
          <button @click="action.onClick">
            <component
              :is="action.icon"
              class="tw-w-5 tw-h-5"
              :class="action.iconClass"
              v-if="action.icon"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {{ action.label }}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('users.titles.attention').toUpperCase()"
      :message="confirmationMessage"
      type="warning"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="confirmAction"
      @cancel="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
import type { UserInList } from '@/_helpers/interfaces';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import { ref } from 'vue';
import { useUserStore } from '@/stores/user.store';
import { useRouter } from 'vue-router';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import EditIcon from '@/assets/icons/Edit.svg';
import CheckMarkSingle from '@/assets/icons/CheckMarkSingle.svg';
import TrashIcon from '@/assets/icons/Trash.svg';

const { t } = useI18n();
const toast = useToast();
const router = useRouter();

const props = defineProps<{
  user: UserInList;
}>();

const emit = defineEmits<{
  reloadTable;
}>();

const { changeUserActivityStatus } = useUserStore();

// Modal state
const showConfirmationModal = ref(false);
const confirmationMessage = ref('');
const pendingAction = ref<{
  type: 'activate' | 'deactivate';
  userName: string;
  userId: number;
} | null>(null);

const handleActivate = () => {
  onChangeUserActivityStatus('activate', props.user.id);
};

const handleDeactivate = () => {
  // Show confirmation modal for deactivation
  confirmationMessage.value = t('users.titles.deactivate_confirmation', {
    usersCount: 'this',
  });

  pendingAction.value = {
    type: 'deactivate',
    userName: props.user.display_name,
    userId: props.user.id,
  };

  showConfirmationModal.value = true;
};

const closeModal = () => {
  showConfirmationModal.value = false;
  pendingAction.value = null;
  confirmationMessage.value = '';
};

const confirmAction = async () => {
  if (!pendingAction.value) return;

  await onChangeUserActivityStatus(pendingAction.value.type, pendingAction.value.userId);
  closeModal();
};

const onChangeUserActivityStatus = async (status: 'activate' | 'deactivate', userId: number) => {
  try {
    const deactivationResult = await changeUserActivityStatus(status, userId);

    emit('reloadTable');

    toast.open({
      message: `User (${props.user.display_name}) ${
        status === 'activate' ? 'activated' : 'deactivated'
      } successfully.`,
      type: 'success',
    });

    if (deactivationResult.warning) {
      toast.open({
        message: deactivationResult.warning,
        type: 'warning',
        duration: 0,
      });
    }
  } catch (e) {
    toast.open({
      message: 'An error occurred while updating user status.',
      type: 'error',
    });
  }
};

const actions = ref<any[]>([
  {
    label: t('users.actions.edit'),
    icon: EditIcon,
    iconClass: 'tw-text-blue-500',
    onClick: () => {
      router.push({ name: 'edit-user', params: { userId: props.user.id } });
    },
    show: () => true,
  },
  {
    label: props.user.active ? t('users.forms.deactivate') : t('users.titles.activate'),
    icon: props.user.active ? TrashIcon : CheckMarkSingle,
    iconClass: props.user.active ? 'tw-text-red-500' : 'tw-text-green-500',
    onClick: () => {
      if (props.user.active) {
        handleDeactivate();
      } else {
        handleActivate();
      }
    },
    show: () => true,
  },
]);
</script>

<style scoped>
/* All styling is handled via Tailwind classes and existing global styles */
</style>
